import type { CSSProperties, ReactNode } from 'react';
import { Button, MultiSelectDropdown } from '@zawarski/eti-ui';
import { useIsMobile } from '@hooks/useIsMobile';
import { LEGACY_MOBILE_SIZE } from './BottomNavigation';
import DialogMultiSelect from './DialogMultiSelect';

/**
 * The legacy EOPA building blocks (rep-ui fragment/SectionContent,
 * components/SectionCapability, fragment/MultiSelectField and the
 * ManageCapabilityModal styles), reproduced with their exact colours, sizes
 * and weights so the EOPA pages read like the original.
 */

export const LEGACY_CARD_SHADOW =
  '0px 0.85px 3px 0px rgba(0, 0, 0, 0.19), 0px 0.25px 1px 0px rgba(0, 0, 0, 0.04)';
export const LEGACY_SECTION_GREY = 'rgba(0, 0, 0, 0.06)';

/** 14px / 700 / uppercase section title: blue on white cards, dark grey on the grey EOPA sections. */
export const legacyTitleStyle = (dark = false): CSSProperties => ({
  color: dark ? '#474747' : '#1C43B1',
  fontSize: 14,
  fontWeight: 700,
  textTransform: 'uppercase',
  lineHeight: 1.5,
});

export const LegacySectionTitle = ({
  children,
  dark,
  suffix,
  style,
}: {
  children: ReactNode;
  dark?: boolean;
  /** Faint trailing word, e.g. legacy "(Risk)" next to "Participant Counties". */
  suffix?: ReactNode;
  style?: CSSProperties;
}) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '1 0 0', ...style }}>
    <span style={legacyTitleStyle(dark)}>{children}</span>
    {suffix ? (
      <span style={{ ...legacyTitleStyle(), color: 'rgba(0, 0, 0, 0.12)' }}>{suffix}</span>
    ) : null}
  </span>
);

/* ------------------------------------------------------------- chip buttons */

export type LegacyChipVariant = 'dark' | 'blue' | 'grey' | 'red' | 'light' | 'print';

const CHIP_STYLES: Record<LegacyChipVariant, CSSProperties> = {
  // StateView/RiskView editBtnContainer: "Add core capability", in the theme's primary blue
  dark: {
    backgroundColor: 'var(--primary)',
    color: 'var(--primary-foreground)',
    fontWeight: 500,
    gap: 8,
  },
  // SectionCapability btnAdd / ManageCapabilityModal "Add new"
  blue: { backgroundColor: '#E2EAFF', color: '#1C43B1', fontWeight: 600, gap: 4 },
  // SectionCapability btnEdit / ManageCapabilityModal btnEdit
  grey: { backgroundColor: '#E0E0E0', color: '#1F1F1F', fontWeight: 600, gap: 4 },
  // SectionContent deleteBtnContainer / SectionCapability btnDelete
  red: { backgroundColor: '#FFF1F4', color: '#BE123C', fontWeight: 700, gap: 4 },
  // SectionContent editBtnContainer (light blue)
  light: { backgroundColor: '#E2EAFF', color: '#1C43B1', fontWeight: 700, gap: 8 },
  // SectionContent printBtnContainer ("Print labels"); disabled -> #F0F0F0 / #C2C2C2
  print: { backgroundColor: '#E0E0E0', color: '#1F1F1F', fontWeight: 700, gap: 8 },
};

const DISABLED_CHIP: CSSProperties = {
  backgroundColor: '#F0F0F0',
  color: '#C2C2C2',
  cursor: 'default',
};

/** The small coloured action chips of the legacy section headers. */
export const LegacyChipButton = ({
  variant,
  icon,
  children,
  onClick,
  padding = '4px 8px',
  hidden,
  disabled,
  style,
  ariaLabel,
}: {
  variant: LegacyChipVariant;
  icon?: ReactNode;
  children?: ReactNode;
  onClick?: () => void;
  padding?: string;
  hidden?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
  ariaLabel?: string;
}) => {
  if (hidden) return null;
  return (
    <span
      role='button'
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        textTransform: 'none',
        fontSize: 12,
        lineHeight: 1.5,
        borderRadius: 4,
        padding,
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        ...CHIP_STYLES[variant],
        ...(disabled ? DISABLED_CHIP : {}),
        ...style,
      }}>
      {children}
      {icon}
    </span>
  );
};

/* ------------------------------------------------------------------- cards */

/**
 * fragment/SectionContent: a card with the title row (title + action chips)
 * and its children. `dark` is the grey EOPA section (bgcolor rgba(0,0,0,.06),
 * #474747 title); otherwise the white card with the blue title.
 */
export const LegacySectionCard = ({
  title,
  titleSuffix,
  dark,
  actions,
  children,
  style,
  className,
}: {
  title: ReactNode;
  titleSuffix?: ReactNode;
  dark?: boolean;
  actions?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}) => {
  const isMobile = useIsMobile(LEGACY_MOBILE_SIZE);
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: isMobile ? 12 : 24,
        gap: 8,
        backgroundColor: dark ? LEGACY_SECTION_GREY : '#FFF',
        borderRadius: 4,
        boxShadow: LEGACY_CARD_SHADOW,
        boxSizing: 'border-box',
        ...style,
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 8,
        }}>
        <LegacySectionTitle dark={dark} suffix={titleSuffix}>
          {title}
        </LegacySectionTitle>
        {actions ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 8 }}>
        {children}
      </div>
    </div>
  );
};

/**
 * components/SectionCapability CapabilityView: the white capability card
 * inside a section (blue uppercase title, Edit / Delete section / Add item
 * chips, grey #EBEBEB body holding the targets or the table).
 */
export const LegacyCapabilityCard = ({
  title,
  actions,
  children,
}: {
  title: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) => {
  const isMobile = useIsMobile(LEGACY_MOBILE_SIZE);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: 12,
        gap: 8,
        backgroundColor: '#FFF',
        borderRadius: 4,
        boxShadow: LEGACY_CARD_SHADOW,
        boxSizing: 'border-box',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 4 }}>
        <span style={{ ...legacyTitleStyle(), flex: '1 0 0', fontSize: isMobile ? 12 : 14 }}>
          {title}
        </span>
        {actions}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          backgroundColor: '#EBEBEB',
          gap: 4,
        }}>
        {children}
      </div>
    </div>
  );
};

/** SectionCapability AddCapability: the empty-targets block with its call to action. */
export const LegacyEmptyTargets = ({
  readOnly,
  onAdd,
}: {
  readOnly: boolean;
  onAdd: () => void;
}) => (
  <div
    style={{
      display: 'flex',
      padding: '48px 16px',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      alignSelf: 'stretch',
      gap: 8,
      border: '1px solid #E0E0E0',
      background: '#F0F0F0',
      textAlign: 'center',
    }}>
    <span style={{ fontSize: 16, fontWeight: 500, color: 'rgba(0, 0, 0, 0.87)' }}>
      Add new targets
    </span>
    <span style={{ fontSize: 14, fontWeight: 300, color: 'rgba(0, 0, 0, 0.87)' }}>
      Use the button below to add a new item
    </span>
    {!readOnly ? (
      <Button className='rep:mt-2' onClick={onAdd}>
        ADD CAPABILITY TARGETS
      </Button>
    ) : null}
  </div>
);

/* ------------------------------------------------------------ county select */

/**
 * fragment/MultiSelectField (react-select, isMulti), on eti-ui's MultiSelectDropdown: the
 * chosen counties show as removable badges. Disabled shows the badges without removing.
 * `inDialog` renders DialogMultiSelect, which keeps the list inside the dialog instead of
 * behind its overlay.
 */
export const LegacyCountySelect = ({
  options,
  value,
  onChange,
  disabled,
  placeholder = 'Select...',
  inDialog,
}: {
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Rendered in an eti-ui dialog (DialogMod): keep the list inside it. */
  inDialog?: boolean;
}) => {
  const Select = inDialog ? DialogMultiSelect : MultiSelectDropdown;
  return (
    <div className='rep:w-full'>
      <Select
        fullWidth
        withBadge
        disabled={disabled}
        options={options.map((county) => ({ label: county, value: county }))}
        value={value}
        onChange={(next) => onChange(next.map(String))}
        placeholder={placeholder}
      />
    </div>
  );
};
